// ABOUTME: Header cell for ShDataGrid3 — handles sort click, right-click pin toggle, per-column filter, and dnd-kit sortable drag.
// ABOUTME: Separate from datagrid2/ShGridHeader.jsx which continues to serve ShDataGrid2 for sidepanel grids.

import { memo, useMemo } from "react";
import { Box, Button } from "@mui/material";
import { useTheme } from "@emotion/react";
import { useDispatch, useSelector } from "react-redux";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { t, default as i18n } from "i18next";
import cx from "classnames";

import SortIcon from "@mui/icons-material/Sort";
import WorkspacesOutlinedIcon from "@mui/icons-material/WorkspacesOutlined";
import ContextMenu from "../contextmenu/ContextMenu";
import SHQuickAction from "../Modal/SHQuickAction";
import PinToggleGroup from "./PinToggleGroup";
import Subheading from "../text/Subheading";
import Text from "../text/Text";
import { tokens } from "../../../theme";
import { useGridFilter } from "../../../contexts/GridFilterContext";
import {
    updateSort,
    setShGridColumns,
    updateFilter,
    updateGroupBy,
} from "../../../newapi/global/filter/gridFilterSlice";
import { setPinned } from "./GridcolumnSettings3";
import { resolveDateGranularities } from "./groupingDateGranularities";
import { useTranslation } from "react-i18next";

/**
 * Per-column filter popover body. Rendered via ContextMenu's `additional` slot
 * when a column declares a `filter` definition.
 */
const ColumnFilterView = ({ filter }) => {
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    const dispatch = useDispatch();
    const { index, id } = useGridFilter();

    const resetFilter = () => {
        dispatch(updateFilter({
            index,
            id,
            filterKey: filter.backend_key,
            value: null,
        }));
    };

    return (
        <Box
            sx={{
                mx: "-12px",
                mt: 1,
                borderTop: `1px solid ${colors.grey[200]}`,
                pt: 3,
                px: 3,
                pb: 1.5,
            }}
        >
            <Box
                sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    pb: 2,
                }}
            >
                <Subheading>{t("filter")}</Subheading>
                <Button
                    onClick={resetFilter}
                    component="button"
                    color={theme.palette.mode === "dark" ? "blue" : "primary"}
                    type="button"
                    sx={{
                        textTransform: "none",
                        height: 32,
                        fontSize: 14,
                        fontFamily: "Roboto, sans-serif",
                        fontWeight: 500,
                        mr: -2,
                        borderRadius: 4,
                    }}
                >
                    {t("reset")}
                </Button>
            </Box>
            <Box sx={{ maxHeight: 300, overflow: "auto" }}>
                <filter.component params={filter.params} />
            </Box>
        </Box>
    );
};

/**
 * ShGridHeader3
 *
 * Props:
 *   column       — the resolved code column definition
 *   sortable     — boolean (default true)
 *   pinnedSide   — "left" | "right" | null; which side the column is pinned to
 *   pinnedOffset — { offsetPx, side } | null; sticky offset computed by parent
 *   filterino    — column.filter definition passed through ({ component, backend_key, params })
 *   dragDisabled — boolean, skip dnd-kit wiring entirely (for zones that should not drag)
 */
const ShGridHeader3 = memo(function ShGridHeader3({
    column,
    sortable = true,
    pinnedSide,
    pinnedOffset,
    filterino,
    dragDisabled = false,
}) {
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    const dispatch = useDispatch();
    const { index, id } = useGridFilter();
    const {t} = useTranslation();

    const filterObject = useSelector((state) => {
        const instance = state.gridFilter.viewInstances[index];
        return instance ? instance[id] : undefined;
    });
    const runtimeColumns = filterObject?.columns;

    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging,
    } = useSortable({
        id: column.field,
        disabled: dragDisabled,
    });

    // Positioning + drag transform go on the wrapper as inline style. They're dynamic
    // (offsetPx comes from a layout computation, transform comes from dnd-kit) so they
    // don't belong in sx.
    const wrapperStyle = {
        position: pinnedOffset ? "sticky" : "relative",
        ...(pinnedOffset && { [pinnedOffset.side]: `${pinnedOffset.offsetPx}px` }),
        zIndex: pinnedOffset ? 3 : undefined,
        transform: CSS.Translate.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : undefined,
        height: "100%",
        width: "100%",
    };

    const sort = () => {
        if (!sortable) return;
        const current = filterObject?.sortModel;
        if (current?.field === column?.field) {
            if (current.type === "ASC") {
                dispatch(updateSort({ index, id, sortModel: { field: column.field, type: "DESC" } }));
            } else if (current.type === "DESC") {
                dispatch(updateSort({ index, id, sortModel: {} }));
            }
        } else {
            dispatch(updateSort({ index, id, sortModel: { field: column.field, type: "ASC" } }));
        }
    };

    const togglePin = (side) => {
        // Toggle: if already pinned to this side, unpin; otherwise pin to this side.
        const nextPinned = pinnedSide === side ? null : side;
        if (!Array.isArray(runtimeColumns)) return;
        const nextColumns = setPinned(runtimeColumns, column.field, nextPinned);
        dispatch(setShGridColumns({ index, id, columns: nextColumns }));
    };

    const pinnedLeft = pinnedSide === "left";
    const pinnedRight = pinnedSide === "right";

    const isActiveSort = column?.field === filterObject?.sortModel?.field;
    const activeSortType = isActiveSort ? filterObject?.sortModel?.type : null;

    return (
        <div
            ref={setNodeRef}
            style={wrapperStyle}
            className={cx("sh-grid-header3", {
                "sh-grid-header3--active": isActiveSort,
                "sh-grid-header3--seam-left":
                    pinnedOffset?.isSeam && pinnedOffset?.side === "left",
                "sh-grid-header3--seam-right":
                    pinnedOffset?.isSeam && pinnedOffset?.side === "right",
            })}
            data-col={column?.field}
        >
            <ContextMenu
                minWidth={380}
                stopPropagation
                additional={
                    <>
                        <PinToggleGroup
                            pinnedLeft={pinnedLeft}
                            pinnedRight={pinnedRight}
                            onPinLeft={() => togglePin("left")}
                            onPinRight={() => togglePin("right")}
                        />
                        {filterino && <ColumnFilterView filter={filterino} />}
                    </>
                }
                options={[]}
                sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    width: "100%",
                    height: "100%",
                    padding: "0 12px",
                    cursor: sortable ? "pointer" : "default",
                    userSelect: "none",
                    fontSize: 14,
                    fontFamily: "Roboto, sans-serif",
                    // Matches ShDataGrid2 MuiDataGrid-columnHeader styling:
                    //   default  → txt.secondary, fontWeight 400
                    //   active   → txt.primary,   fontWeight 500 (with a subtle text-shadow)
                    color: isActiveSort ? colors.txt.primary : colors.txt.secondary,
                    fontWeight: isActiveSort ? 500 : 400,
                    textShadow: isActiveSort ? `0px 0px 1px ${colors.txt.primary}` : undefined,
                }}
            >
                <Box
                    {...attributes}
                    {...listeners}
                    onClick={sort}
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        width: 1,
                        gap: 1,
                        touchAction: dragDisabled ? "auto" : "none",
                    }}
                >
                    {/* Hovering a header shows its full title, so a column that is too
                        narrow for its label stays readable through the ellipsis. */}
                    <SHQuickAction
                        disableScrollLock={true}
                        component={<Text>{column?.headerName ?? column?.field}</Text>}
                    >
                        {({anchorEl, onMouseEnter, onMouseLeave, onTouchStart}) => (
                            <Box
                                aria-owns={anchorEl ? 'simple-popover' : undefined}
                                aria-haspopup={false}
                                onMouseEnter={onMouseEnter}
                                onMouseLeave={onMouseLeave}
                                onTouchStart={onTouchStart}
                                sx={{
                                    whiteSpace: "nowrap",
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                }}
                            >
                                {column?.headerName ?? column?.field}
                            </Box>
                        )}
                    </SHQuickAction>

                    {isActiveSort && (
                        <Box
                            sx={{
                                flexShrink: 0,
                                width: 24,
                                height: 24,
                                minWidth: 24,
                                borderRadius: "16px",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                background:
                                    theme.palette.mode === "dark"
                                        ? colors.blue[100]
                                        : colors.primary[100],
                                color:
                                    theme.palette.mode === "dark"
                                        ? colors.blue[400]
                                        : colors.primary[400],
                                transform: activeSortType === "DESC" ? "rotate(180deg) scaleX(-1)" : undefined,
                            }}
                        >
                            <SortIcon fontSize="small" />
                        </Box>
                    )}
                </Box>
            </ContextMenu>
        </div>
    );
});

export default ShGridHeader3;
